import React, { useCallback, useEffect, useState } from "react";
import { fetcher } from "../../../services/api";
import { useUser } from "../../../contexts/UserContext";
import AppModal from "../../AppModal";
import AppModalHeader from "../../AppModal/AppModalHeader";
import AppModalContent from "../../AppModal/AppModalContent";
import AppInput from "../../AppInput";
import AppCheckbox from "../../AppCheckbox";
import AppButton from "../../AppButton";
import { useValidationErrors } from "../../../hooks/useValidationErrors";
import Loader from "../../Loader/Loader";
import AddPortalModal from "../AddPlatfromModal";
import "./CreateKeyModal.css";

interface CreateKeyModalProps {
  isVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  onKeyCreated?: () => void;
  editData?: any;
}

const CreateKeyModal: React.FC<CreateKeyModalProps> = ({
  isVisible,
  setIsModalVisible,
  onKeyCreated,
  editData,
}) => {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    key_usage_description: "",
    domain: "",
    agreed_to_terms: false,
  });
  const { errors, setApiErrors, clearError } =
    useValidationErrors<typeof formData>();
  const [loading, setLoading] = useState(false);
  const [isAddPlatformModalVisible, setIsAddPlatformModalVisible] =
    useState(false);
  const [groupKeyForPlatform, setGroupKeyForPlatform] = useState<string | null>(
    null
  );

  const updateFormData = useCallback(
    (key: keyof typeof formData, value: any) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      clearError(key);
    },
    [clearError]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      name: formData.name,
      email: formData.email,
      description: formData.description,
      domain: formData.domain,
      key_usage_description: formData.description,
      agreed_to_terms: formData.agreed_to_terms,
    };

    const url = editData
      ? `/publisher/client/update/${editData.key}`
      : "/publisher/client/create";

    const { data, error } = await fetcher<any>(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (data) {
      setLoading(false);
      if (!editData) {
        setGroupKeyForPlatform(data.data.key);
        onKeyCreated?.();
        resetForm();
        setIsModalVisible(false);
      } else {
        onKeyCreated?.();
        resetForm();
        setIsModalVisible(false);
      }
    } else if (error) {
      setApiErrors(error);
      setLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      email: formData.email || (user as any)?.email || "",
      description: "",
      key_usage_description: "",
      domain: "",
      agreed_to_terms: false,
    });
    setApiErrors({});
  }, [formData.email, setApiErrors, user]);

  const handlePlatformCreated = useCallback(() => {
    onKeyCreated?.(); 
    setGroupKeyForPlatform(null); 
  }, [onKeyCreated]);

  useEffect(() => {
    if (user && (user as any).email) {
      setFormData((prev) => ({ ...prev, email: (user as any).email }));
    }
  }, [user]);

  useEffect(() => {
    if (groupKeyForPlatform) {
      setIsAddPlatformModalVisible(true);
    }
  }, [groupKeyForPlatform]);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        email: editData.email || (user as any)?.email || "",
        description: editData.description || "",
        key_usage_description: editData.key_usage_description || "",
        domain: editData.domain || "",
        agreed_to_terms: editData.agreed_to_terms || false,
      });
    }
  }, [editData, user]);

  const handleAddPlatformModalClose = useCallback((visible: boolean) => {
    setIsAddPlatformModalVisible(visible);
    if (!visible) {
      setGroupKeyForPlatform(null);
    }
  }, []);
  
  return (
    <>
      <AppModal
        isVisible={isVisible}
        onChange={() => {
          setIsModalVisible(!isVisible);
          resetForm();
        }}
      >
        <AppModalHeader
          onClose={() => {
            setIsModalVisible(!isVisible);
            resetForm();
          }}
        >
          {editData ? "Update" : "Add"} your platform details
        </AppModalHeader>
        <AppModalContent>
          <div className="modal-description">
            {editData ? "Update" : "Add"} your platform details to generate a
            test API key for each app (limited to 100 API calls). When you're
            ready to launch, you can request an upgrade to a production key for
            unlimited access.
          </div>

          <form className="modal-form" onSubmit={handleSubmit}>
            <AppInput
              id="platformName"
              label="Platform name"
              value={formData.name}
              onChange={(e) => updateFormData("name", e.target.value)}
              error={errors.name}
            />

            <AppInput
              id="email"
              label="Your email"
              value={formData.email}
              onChange={(e) => updateFormData("email", e.target.value)}
              error={errors.email}
            />

            <AppInput
              id="website"
              label="Platform website"
              value={formData.domain}
              onChange={(e) => updateFormData("domain", e.target.value)}
              error={errors.domain}
            />

            <AppInput
              id="description"
              value={formData.description}
              onChange={(e) => updateFormData("description", e.target.value)}
              error={errors.description}
              placeholder="Briefly tell us about your platform and how you plan to integrate KLIPY into your product."
              textarea
            />
            {!editData && (
              <>
                <AppCheckbox
                  name="terms"
                  checked={formData.agreed_to_terms}
                  onChange={(e) =>
                    updateFormData("agreed_to_terms", e.target.checked)
                  }
                  value="agreed"
                  secondary
                  nohover
                  error={!!errors.agreed_to_terms}
                >
                  By checking this box, I agree to the{" "}
                  <a
                    href="https://klipy.com/support/api-terms"
                    target="_blank"
                    rel="noreferrer"
                  >
                    KLIPY API Terms.
                  </a>
                </AppCheckbox>
                {errors.agreed_to_terms && (
                  <div className="error-text">
                    Required to agree to terms of service to proceed.
                  </div>
                )}
              </>
            )}
            <AppButton
              variant="secondary"
              type="submit"
              fullWidth
              disabled={loading}
            >
              {loading ? (
                <Loader />
              ) : editData ? (
                "Update Platform"
              ) : (
                "Add Platform"
              )}
            </AppButton>
          </form>
        </AppModalContent>
      </AppModal>
      {!editData && (
        <AddPortalModal
          isVisible={isAddPlatformModalVisible}
          setIsModalVisible={handleAddPlatformModalClose}
          groupKey={groupKeyForPlatform}
          onPlatformCreate={handlePlatformCreated}
        />
      )}
    </>
  );
};

export default CreateKeyModal;
